import type { CssAtRule, CssNode } from '../types'
import type { BreakpointWithOwner } from '../../breakpoints'
import { buildCssAtRule } from '../utils'
import type { Breakpoint } from '@wix/document-services-types'

const buildMediaQueryParams = (breakpoint: BreakpointWithOwner): string => {
    const parts: string[] = []
    if (breakpoint.min !== undefined && breakpoint.min > 0) {
        parts.push(`(min-width: ${breakpoint.min}px)`)
    }
    if (breakpoint.max !== undefined) {
        parts.push(`(max-width: ${breakpoint.max}px)`)
    }
    return parts.join(' and ')
}

const MIN_WIDTH_PATTERN = /\(min-width:\s*(\d+)px\)/
const MAX_WIDTH_PATTERN = /\(max-width:\s*(\d+)px\)/

const parseMediaQueryParams = (params: string): Breakpoint => {
    const result: Breakpoint = {}

    const minMatch = params.match(MIN_WIDTH_PATTERN)
    if (minMatch?.[1]) {
        result.min = +minMatch[1]
    }

    const maxMatch = params.match(MAX_WIDTH_PATTERN)
    if (maxMatch?.[1]) {
        result.max = +maxMatch[1]
    }

    return result
}

enum AtRuleName {
    MEDIA = 'media',
    SCOPE = 'scope',
    LAYER = 'layer',
}

export const buildBreakpointAst = (nodes: CssNode[], breakpoint: BreakpointWithOwner): CssNode => {
    const mediaQuery = buildCssAtRule(AtRuleName.MEDIA, { params: buildMediaQueryParams(breakpoint), nodes })

    if (breakpoint.ownerComponentId) {
        const scopeRule = buildCssAtRule(AtRuleName.SCOPE, {
            params: `(#${breakpoint.ownerComponentId})`,
            nodes: [mediaQuery],
        })
        return scopeRule
    }

    return mediaQuery
}

const SCOPE_OWNER_PATTERN = /\(#([^)]+)\)/

const parseScopeOwner = (params: string): string | undefined => params.match(SCOPE_OWNER_PATTERN)?.[1]

export const parseBreakpointAst = (atRule: CssAtRule): { breakpoint: BreakpointWithOwner; nodes: CssNode[] } => {
    if (atRule.name === AtRuleName.SCOPE) {
        const ownerComponentId = parseScopeOwner(atRule.params ?? '')
        const mediaNode = atRule.nodes[0] as CssAtRule
        const { breakpoint, nodes } = parseBreakpointAst(mediaNode)
        return {
            breakpoint: { ...breakpoint, ownerComponentId },
            nodes,
        }
    }

    if (atRule.name === AtRuleName.MEDIA) {
        return { breakpoint: parseMediaQueryParams(atRule.params ?? ''), nodes: atRule.nodes }
    }

    throw new Error(`Unsupported at-rule: ${atRule.name}`)
}

const VARIABLES_AT_RULE_PARAMS = 'variables'

export const isVariablesLayer = (atRule: CssAtRule): boolean =>
    atRule.name === AtRuleName.LAYER && atRule.params === VARIABLES_AT_RULE_PARAMS
export const buildVariablesLayer = (nodes: CssNode[]): CssNode =>
    buildCssAtRule(AtRuleName.LAYER, { params: VARIABLES_AT_RULE_PARAMS, nodes })
